import { useMemo } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageSeo } from './usePageSeo';
import { JsonLd } from './JsonLd';
import { organizationSchema, websiteSchema, breadcrumbSchema } from './schemas';
import { NOINDEX_PREFIXES, type SeoLang } from './config';
import { resolveClientLang } from '../lib/localeDisplay';
import { pickCopy, staticSeoCopy } from './staticPages';

function isNoIndexPath(pathname: string): boolean {
  return NOINDEX_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

/**
 * SEO global par route. Les pages shop gèrent leurs propres meta (données API).
 * Les pages légales / landing / home sont couvertes ici ; ShopPage surcharge via usePageSeo.
 */
export default function RouteSeo() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const lang = resolveClientLang(i18n.language) as SeoLang;

  const shopMatch = matchPath('/shop/:shopSlug', pathname);
  const noindex = isNoIndexPath(pathname) || pathname === '/login' || pathname === '/register' || pathname === '/registrieren';

  const page = useMemo(() => {
    if (shopMatch) {
      // ShopPage applique les meta dynamiques ; placeholder pendant le chargement
      const c = pickCopy(staticSeoCopy.shop, lang);
      return {
        title: c.title,
        description: c.description,
        path: pathname,
        noindex: false,
        skipGlobalJsonLd: false,
      };
    }

    if (pathname === '/' || pathname === '/client') {
      const c = pickCopy(staticSeoCopy.home, lang);
      return { ...c, path: '/', noindex: false, skipGlobalJsonLd: false };
    }

    if (pathname === '/landing') {
      const c = pickCopy(staticSeoCopy.landing, lang);
      return { ...c, path: '/landing', noindex: false, skipGlobalJsonLd: false };
    }

    if (pathname === '/support') {
      const c = pickCopy(staticSeoCopy.support, lang);
      return { ...c, path: '/support', noindex: false, skipGlobalJsonLd: false };
    }

    const legalKey = pathname.replace(/^\//, '') as keyof typeof staticSeoCopy.legal;
    if (staticSeoCopy.legal[legalKey]) {
      const c = pickCopy(staticSeoCopy.legal[legalKey], lang);
      return { ...c, path: pathname, noindex: false, skipGlobalJsonLd: false };
    }

    if (pathname === '/login' || pathname === '/verify-email') {
      const c = pickCopy(staticSeoCopy.auth, lang);
      return { ...c, path: pathname, noindex: true, skipGlobalJsonLd: true };
    }

    if (pathname === '/register' || pathname === '/registrieren') {
      const c = pickCopy(staticSeoCopy.register, lang);
      return { ...c, path: pathname, noindex: true, skipGlobalJsonLd: true };
    }

    if (pathname === '/forgot-password') {
      const c = pickCopy(staticSeoCopy.forgot, lang);
      return { ...c, path: pathname, noindex: true, skipGlobalJsonLd: true };
    }

    if (pathname === '/reset-password') {
      const c = pickCopy(staticSeoCopy.reset, lang);
      return { ...c, path: pathname, noindex: true, skipGlobalJsonLd: true };
    }

    if (noindex) {
      return {
        title: 'BienLivrer',
        description: pickCopy(staticSeoCopy.home, lang).description,
        path: pathname,
        noindex: true,
        skipGlobalJsonLd: true,
      };
    }

    const c = pickCopy(staticSeoCopy.notFound, lang);
    return { ...c, path: pathname, noindex: true, skipGlobalJsonLd: true };
  }, [pathname, lang, shopMatch, noindex]);

  // ShopPage possède les meta dynamiques — ne pas les écraser ici.
  usePageSeo({
    enabled: !shopMatch,
    title: page.title,
    description: page.description,
    path: page.path,
    noindex: page.noindex,
    lang,
    hreflang: !page.noindex,
  });

  const crumbs = useMemo(() => {
    if (shopMatch) return null;
    if (pathname === '/' || pathname === '/client') {
      return [{ name: 'BienLivrer', path: '/' }];
    }
    if (pathname === '/landing') {
      return [
        { name: 'BienLivrer', path: '/' },
        { name: 'Landing', path: '/landing' },
      ];
    }
    if (staticSeoCopy.legal[pathname.replace(/^\//, '') as keyof typeof staticSeoCopy.legal] || pathname === '/support') {
      return [
        { name: 'BienLivrer', path: '/' },
        { name: page.title.split('—')[0].trim(), path: pathname },
      ];
    }
    return null;
  }, [pathname, page.title, shopMatch]);

  return (
    <>
      {!page.skipGlobalJsonLd && (
        <>
          <JsonLd id="organization" data={organizationSchema()} />
          <JsonLd id="website" data={websiteSchema()} />
        </>
      )}
      {crumbs && <JsonLd id="breadcrumb" data={breadcrumbSchema(crumbs)} />}
    </>
  );
}
